import { Controller, useForm } from "react-hook-form";
import {
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import TextField from "@mui/material/TextField";
import * as React from "react";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { useDispatch, useSelector } from "react-redux";
import { createTask } from "../redux/Slices/TasksSlice";
import dayjs from "dayjs";
import toast, { Toaster } from "react-hot-toast";
import { fetchCategory } from "../redux/Slices/CategorySlice";

function AddTaskPage() {
  const dispatch = useDispatch();
  const { categories } = useSelector((state) => state.categoriesReducer);

  const {
    control,
    register,
    handleSubmit,
    clearErrors,
    setError,
    formState: { errors, isSubmitting },
    setValue,
    reset,
  } = useForm({
    mode: "onChange",
    defaultValues: {
      taskName: "",
      categories: "",
      Status: "",
      duedate: "",
      createddate: "",
      description: "",
    },
  });

  const [selectedDate, setSelectedDate] = React.useState(null);

  const handleDateChange = (DateValue) => {
    setSelectedDate(DateValue);
    setValue("duedate", DateValue ? dayjs(DateValue).toISOString() : "", {
      shouldValidate: true,
    });
  };

  const onSubmit = async (data) => {
    try {
      const taskData = {
        ...data,
        Date: selectedDate ? dayjs(selectedDate).format("DD/MM/YYYY") : null,
        createddate: dayjs().format("DD/MM/YYYY"),
      };

      await dispatch(createTask(taskData));
      await dispatch(fetchCategory());

      toast.success("Task created successfully ");
      reset({
        taskName: "",
        categories: "",
        Status: "",
        duedate: "",
        description: "",
      });
      setSelectedDate(null);
    } catch (error) {
      console.error("Failed to create task:", error);
      toast.error("Failed to create task");
    }

    console.log(data);
  };

  return (
    <>
      <Toaster />
      <div className="container-fluid mt-3 d-flex flex-col align-items-center">
        <h2 className="text-center mb-4">Create Task</h2>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="border p-4 rounded shadow-sm w-75"
        >
          {/* Task Name */}
          <Box component="div" sx={{ mt: 1, mb: 1 }}>
            <TextField
              id="taskName"
              label="Task Name"
              variant="outlined"
              fullWidth
              error={!!errors.taskName}
              helperText={errors.taskName?.message}
              {...register("taskName", {
                required: "Task name is required",
                maxLength: {
                  value: 9,
                  message: "Task name cannot exceed 9 characters",
                },
              })}
            />
          </Box>
          {/* Description */}
          <Box component="div" sx={{ mt: 1, mb: 1 }}>
            <TextField
              id="description"
              label="Description"
              variant="outlined"
              fullWidth
              multiline
              rows={4}
              error={!!errors.description}
              helperText={errors.description?.message}
              {...register("description", {
                required: "Description is required",
              })}
            />
          </Box>
          {/* Categories */}
          <FormControl
            sx={{ mt: 1, mb: 1, minWidth: "100%" }}
            error={!!errors.categories}
          >
            <InputLabel id="categories-label">Categories</InputLabel>
            <Controller
              name="categories"
              control={control}
              rules={{ required: "Categories is required" }}
              render={({ field }) => (
                <Select
                  {...field}
                  labelId="categories-label"
                  id="categories"
                  fullWidth
                  label="Categories"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={category.name}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
            <FormHelperText>{errors.categories?.message}</FormHelperText>
          </FormControl>

          {/* Status */}
          <FormControl
            sx={{ mt: 1, mb: 1, minWidth: "100%" }}
            error={!!errors.Status}
          >
            <InputLabel id="Status-label">Status</InputLabel>
            <Controller
              name="Status"
              control={control}
              rules={{ required: "Status is required" }}
              render={({ field }) => (
                <Select
                  {...field}
                  labelId="Status-label"
                  id="Status"
                  fullWidth
                  label="Status"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  <MenuItem value="Pending">Pending</MenuItem>
                  <MenuItem value="Completed">Completed</MenuItem>
                </Select>
              )}
            />
            <FormHelperText>{errors.Status?.message}</FormHelperText>
          </FormControl>

          {/* Due Date */}
          <FormControl
            sx={{ mt: 1, mb: 1, minWidth: "100%" }}
            error={!!errors.duedate}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Due Date"
                value={selectedDate || null}
                onChange={(newValue) => {
                  const today = dayjs().startOf("day");
                  if (newValue && dayjs(newValue).isBefore(today)) {
                    setError("duedate", {
                      type: "manual",
                      message: "You cannot select a past date.",
                    });
                    setSelectedDate(null);
                  } else {
                    clearErrors("duedate");
                    handleDateChange(newValue);
                  }
                }}
                slots={{
                  textField: (params) => (
                    <TextField {...params} fullWidth error={!!errors.duedate} />
                  ),
                }}
              />
            </LocalizationProvider>

            <FormHelperText>{errors.duedate?.message}</FormHelperText>
          </FormControl>

          {/* Hidden Created Date */}
          <input type="hidden" {...register("createddate")} />

          {/* Submit and Cancel Buttons */}
          <div className="d-flex gap-5">
            <button type="submit" className="btn btn-primary w-100">
              {isSubmitting ? "Adding..." : "Add "}
            </button>
            <button
              type="button"
              onClick={() => {
                reset();
                setSelectedDate(null);
              }}
              className="btn btn-danger w-100"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </>
  );
}

export default AddTaskPage;
